/**
 * useSelectionSync Composable
 *
 * Unified selection synchronization logic for all Browser components.
 * Provides two sync strategies:
 *
 * 1. 'reactive' - Continuously syncs with parent (BoardBrowser, UserBrowser)
 *    - Uses deep equality check to prevent race conditions
 *    - Needed when parent state changes during navigation/filtering
 *    - Re-syncs when items change (e.g., after folder navigation)
 *
 * 2. 'initialize-once' - Sets initial selection only (6 other browsers)
 *    - Parent hands off control to Browser after init
 *    - Simpler pattern with less reactive overhead
 */

import { watch, onMounted, nextTick, unref, toValue, type Ref } from 'vue';
import type { BaseBrowserAdapter } from '../adapters/BaseBrowserAdapter';

export type SelectionSyncStrategy = 'reactive' | 'initialize-once';

export interface UseSelectionSyncOptions<T> {
  /**
   * Adapter instance to sync selections with
   */
  adapter: BaseBrowserAdapter<T>;

  /**
   * Current selection mode
   */
  mode: 'single' | 'multi';

  /**
   * Sync strategy to use
   * - 'reactive': Continuous sync with parent via deep equality watcher
   * - 'initialize-once': One-time initialization only
   */
  syncStrategy: SelectionSyncStrategy;

  /**
   * Selected IDs for multi-mode (optional)
   * Can be a plain array or a Ref for reactive sync
   */
  selectedIds?: string[] | Ref<string[]>;

  /**
   * Selected item for single-mode (optional)
   */
  selectedItem?: T | null;

  /**
   * Selected items for multi-mode initialize-once (optional)
   */
  selectedItems?: T[];

  /**
   * Method to get ID from item (for single-mode reactive sync)
   */
  getItemId?: (item: T) => string;

  /**
   * All items available for selection (for single-mode reactive sync)
   */
  allItems?: T[];
}

/**
 * Unified selection sync composable
 *
 * @example
 * // Pattern A: Reactive sync (BoardBrowser, UserBrowser)
 * useSelectionSync({
 *   adapter,
 *   mode: props.mode,
 *   syncStrategy: 'reactive',
 *   selectedIds: props.selectedIds,
 * });
 *
 * @example
 * // Pattern B: Initialize-once (LabelBrowser, etc.)
 * useSelectionSync({
 *   adapter,
 *   mode: props.mode,
 *   syncStrategy: 'initialize-once',
 *   selectedItem: props.selectedItem,
 *   selectedItems: props.selectedItems,
 * });
 */
export function useSelectionSync<T>(options: UseSelectionSyncOptions<T>): void {
  const {
    adapter,
    mode,
    syncStrategy,
    selectedIds = [],
    selectedItem,
    selectedItems = [],
    getItemId,
    allItems = [],
  } = options;

  if (syncStrategy === 'reactive') {
    // Pattern A: Reactive sync with deep equality check
    if (mode === 'multi') {
      // Multi-mode: Watch selectedIds with deep equality
      // Support both plain arrays and Refs for reactive sync
      watch(
        () => unref(selectedIds),
        (newIds, oldIds) => {
          // Initial mount - sync once
          if (!oldIds) {
            adapter.setSelectedIds(newIds || []);
            return;
          }

          // Deep equality check - CRITICAL for preventing selection clearing
          // Parent re-renders create new array references with same content
          const newSet = new Set(newIds || []);
          const oldSet = new Set(oldIds);

          // Check size first (optimization)
          if (newSet.size !== oldSet.size) {
            adapter.setSelectedIds(newIds || []);
            return;
          }

          // Same size - check if all elements match
          for (const id of newSet) {
            if (!oldSet.has(id)) {
              // Found a new ID - need to sync
              adapter.setSelectedIds(newIds || []);
              return;
            }
          }

          // IDs identical - skip sync to preserve adapter's independent selection cache
        },
        { immediate: true }
      );

      // Watch for item changes (e.g., after folder navigation)
      // When new items become available, check if any should be marked as selected
      // This fixes the bug where selections appear unchecked after navigating folders
      const reactiveState = adapter.getReactiveState();
      watch(
        () => toValue(reactiveState.items),
        (newItems) => {
          if (!newItems || newItems.length === 0) return;

          const targetIds = new Set(unref(selectedIds) || []);
          if (targetIds.size === 0) return;

          // Check each new item - if its ID is in selectedIds but not in adapter's selection,
          // add it to the adapter's selection
          for (const item of newItems) {
            const itemId = adapter.itemConfig.getId(item);
            if (targetIds.has(itemId) && !adapter.isItemSelected(item)) {
              // This item should be selected but isn't - select it
              adapter.selectItem(item);
            }
          }
        },
        { immediate: false } // Don't run immediately - initial sync handled by selectedIds watch
      );
    } else {
      // Single-mode: Watch selectedItem
      watch(
        () => selectedItem,
        (item) => {
          if (item && getItemId && allItems.length > 0) {
            const itemId = getItemId(item);
            const matchedItem = allItems.find((i) => getItemId(i) === itemId);
            if (matchedItem) {
              adapter.selectItem(matchedItem);
            }
          } else if (!item) {
            // Handle clearing: when selectedItem becomes null/undefined, clear adapter selection
            adapter.clearSelection();
          }
        },
        { immediate: true }
      );
    }
  } else {
    // Pattern B: Initialize-once
    onMounted(async () => {
      await nextTick();

      if (mode === 'single' && selectedItem) {
        adapter.selectItem(selectedItem);
      } else if (mode === 'multi' && selectedItems.length > 0) {
        selectedItems.forEach((item) => adapter.selectItem(item));
      }
    });
  }
}
